import { ChatAppResponseOrError, ChatAppRequest, signInRequest, ResponseChoice, ResponseMessage} from "./models";

const BACKEND_URI = "https://api.dev.rag.codeprime.dev";
// Use relative path in dev (for proxy) or full URL in production
const WS_BASE = import.meta.env.DEV ? (window.location.protocol === 'https:' ? 'wss:' : 'ws:') + '//' + window.location.host : (BACKEND_URI.replace('https://', 'wss://').replace('http://', 'ws://'));

function getHeaders(idToken: string | undefined): Record<string, string> {
    var headers: Record<string, string> = {
        "Content-Type": "application/json"
    };
    return headers;
}

export async function chatApi(request: any, token: string): Promise<Response> {
    return await fetch(`${BACKEND_URI}/api/v1/clients/current/chat/`, {
        method: "POST",
        headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
        body: JSON.stringify(request)
    });
}
export async function signInApi(request: signInRequest): Promise<Response> {
    return await fetch(`${BACKEND_URI}/api/v1/auth/token/`, {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify(request)
    });
}
export async function getSampleQuestions(token: string): Promise<Response> {
    return await fetch(`${BACKEND_URI}/api/v1/users/current/`, {
        method: "GET",
        headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`},
    });
}
export async function getDocumentApi(filename: string, token: string): Promise<Response> {
    return await fetch(`${BACKEND_URI}/api/v1/clients/current/documents/?filename=${encodeURIComponent(filename)}`, {
        method: "GET",
        headers: {"Content-Type": "application/json", Authorization: `Bearer ${token}`}
    });
}
export async function UploadDocumentsApi(request: any): Promise<Response> {
    let formObjectInside = Object.fromEntries(request.entries());
    return await fetch(`${BACKEND_URI}/upload_documents`, {
        method: "POST",
        headers: {"Content-Type": "multipart/form-data"},
        body: request
    });
}

export function getCitationFilePath(citation: string): string {
    return `${BACKEND_URI}/content/${citation}`;
};

export type StreamMessage = {
    type: 'status' | 'reasoning' | 'answer' | 'complete' | 'error' | 'done';
    content?: string;
    message?: ResponseMessage;
    question?: string;
    generated_question?: string;
    source_documents?: any[];
    error?: string;
    metadata?: {
        documents?: any[];
        source_documents?: any[];
        [key: string]: any;
    };
};

export type WebSocketCallbacks = {
    onStatus: (status: string) => void;
    onReasoning: (reasoning: string) => void;
    onAnswer: (chunk: Partial<ResponseChoice>) => void;
    onError: (error: Error) => void;
    onComplete: (finalAnswer: ResponseChoice) => void;
    onClose?: () => void;
};

export function chatApiStream(
    request: ChatAppRequest,
    token: string,
    callbacks: WebSocketCallbacks
): () => void {
    let abortController: AbortController | null = new AbortController();
    let accumulatedAnswer: Partial<ResponseChoice> = {
        message: { content: '', role: 'assistant' },
        question: request.question,
        generated_question: '',
        source_documents: []
    };
    let accumulatedReasoning = '';

    const apiUrl = import.meta.env.DEV 
        ? `/api/v1/clients/current/chat/` 
        : `${BACKEND_URI}/api/v1/clients/current/chat/`;

    fetch(apiUrl, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(request),
        signal: abortController.signal
    })
    .then(async (response) => {
        if (response.status === 401) {
            callbacks.onError(new Error('Unauthorized'));
            return;
        }

        if (!response.ok) {
            const error = await response.json().catch(() => ({ error: 'Unknown error' }));
            callbacks.onError(new Error(error.error || 'Request failed'));
            return;
        }

        // Read the response as a stream
        const reader = response.body?.getReader();
        const decoder = new TextDecoder();

        if (!reader) {
            callbacks.onError(new Error('No response body'));
            return;
        }

        let buffer = '';

        while (true) {
            const { done, value } = await reader.read();
            
            if (done) {
                // Stream ended - send final answer if we have accumulated data
                if (accumulatedAnswer.message?.content) {
                    const finalAnswer: ResponseChoice = {
                        message: accumulatedAnswer.message || { content: '', role: 'assistant' },
                        question: request.question,
                        generated_question: accumulatedAnswer.generated_question || '',
                        source_documents: accumulatedAnswer.source_documents || []
                    };
                    callbacks.onComplete(finalAnswer);
                } else {
                    callbacks.onClose?.();
                }
                break;
            }

            // Decode chunk and add to buffer
            buffer += decoder.decode(value, { stream: true });
            
            // Process complete SSE messages (SSE format: "data: {...}\n\n")
            const lines = buffer.split('\n');
            buffer = lines.pop() || ''; // Keep incomplete line in buffer

            for (const line of lines) {
                if (!line.trim()) continue; // Skip empty lines
                
                // SSE format: "data: {json}"
                if (line.startsWith('data: ')) {
                    const jsonStr = line.substring(6); // Remove "data: " prefix
                    
                    try {
                        const data: StreamMessage = JSON.parse(jsonStr);
                        
                        // Handle different message types
                        if (data.type === 'status') {
                            // Status message - show in loading state
                            if (data.content) {
                                callbacks.onStatus(data.content);
                            }
                        } else if (data.type === 'reasoning') {
                            // Reasoning message - accumulate for ThoughtProcess
                            if (data.content) {
                                accumulatedReasoning += data.content;
                                callbacks.onReasoning(accumulatedReasoning);
                            }
                        } else if (data.type === 'answer') {
                            // Answer chunk - accumulate for answer display
                            if (data.content) {
                                accumulatedAnswer.message = {
                                    ...accumulatedAnswer.message!,
                                    content: (accumulatedAnswer.message?.content || '') + data.content
                                };
                            }
                            if (data.source_documents) {
                                accumulatedAnswer.source_documents = data.source_documents;
                            }
                            if (data.generated_question !== undefined) {
                                accumulatedAnswer.generated_question = data.generated_question;
                            }
                            callbacks.onAnswer({ ...accumulatedAnswer });
                        } else if (data.type === 'complete') {
                            // Complete message received
                            const finalAnswer: ResponseChoice = {
                                message: data.message || accumulatedAnswer.message || { content: '', role: 'assistant' },
                                question: data.question || request.question,
                                generated_question: data.generated_question || accumulatedAnswer.generated_question || '',
                                source_documents: data.source_documents || accumulatedAnswer.source_documents || []
                            };
                            callbacks.onComplete(finalAnswer);
                            return;
                        } else if (data.type === 'done') {
                            // Done message - extract source_documents from metadata
                            if (data.metadata?.source_documents) {
                                accumulatedAnswer.source_documents = data.metadata.source_documents.map(item => ({
                                    ...item.metadata,
                                    page_content: item.page_content
                                }));
                                // Update the answer with the documents
                                callbacks.onAnswer({ ...accumulatedAnswer });
                            }
                        } else if (data.type === 'error') {
                            callbacks.onError(new Error(data.error || 'Unknown error'));
                            return;
                        }
                    } catch (e) {
                        // If not JSON, might be plain text in SSE format
                        console.warn('Failed to parse SSE data:', jsonStr, e);
                    }
                } else if (line.startsWith('event: ')) {
                    // Handle SSE event types if needed
                    // event: message\n
                } else if (line.startsWith('id: ')) {
                }
            }
        }
    })
    .catch((error) => {
        if (error.name === 'AbortError') {
            return; // Request was cancelled
        }
        callbacks.onError(error instanceof Error ? error : new Error('Request failed'));
    });

    // Return cleanup function
    return () => {
        if (abortController) {
            abortController.abort();
            abortController = null;
        }
    };
}
